import React, { useState, useEffect } from "react";
import { Card, CardBody, CardHeader, Row, Col, Input, Label, FormGroup, Button, Badge, FormFeedback
} from "reactstrap";
import { useFormik } from "formik";
import { activitySchema, activityDefaults } from "../../../../models/activity";
import ObjectiveGrid from "./ObjectiveGrid";
import PropTypes from "prop-types";
import { FormPhotoUpload, OverlayLoader, LoadingButton } from "../../../../components/Common";
import { GLOBAL_STATUS } from "../../../../constants/common";
import { GLOBAL_STATUS_LABELS } from "../../../../constants/common";;

/**
 * ActivityEditor
 * Editor completo: Dados básicos + Malha Pedagógica (Objetivos e Tópicos)
 */
const ActivityEditor = ({ activity, onSave, onDelete, isSaving, onChange }) => {
    const [localObjectives, setLocalObjectives] = useState([]);

    const formik = useFormik({
        initialValues: activityDefaults,
        validationSchema: activitySchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            onSave({ ...values, objectives: localObjectives });
        },
    });

    // Sincroniza estado quando a atividade selecionada muda
    useEffect(() => {
        if (activity) {
            formik.resetForm({
                values: {
                    id: activity.id || "",
                    name: activity.name || "",
                    description: activity.description || "",
                    color: activity.color || "#3b82f6",
                    photoUrl: activity.photoUrl || null,
                    order: activity.order || 0,
                    xp: activity.xp || 0,
                    status: activity.status || GLOBAL_STATUS.ACTIVE,
                    evaluationSystem: !!activity.evaluationSystem,
                    createdAt: activity.createdAt || null,
                    createdBy: activity.createdBy || null
                }
            });
            setLocalObjectives(activity.objectives || []);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activity]);

    // Notifica mudança para o pai (isDirty)
    useEffect(() => {
        onChange(formik.dirty);
    }, [formik.dirty, onChange]);

    // Recalcula o total de XP sempre que os objetivos mudam
    useEffect(() => {
        let totalXp = 0;
        localObjectives.forEach(obj => {
            (obj.topics || []).forEach(topic => {
                totalXp += (Number(topic.xp) || 0);
            });
        });
        if (formik.values.xp !== totalXp) {
            formik.setFieldValue("xp", totalXp);
        }
    }, [localObjectives, formik.values.xp, formik]);

    if (!activity) {
        return (
            <div className="h-100 d-flex align-items-center justify-content-center bg-light-subtle rounded-0 border border-dashed p-5">
                <div className="text-center">
                    <div className="avatar-lg mx-auto mb-4">
                        <div className="avatar-title bg-primary-subtle text-primary rounded-circle display-4">
                            <i className="mdi mdi-run-fast"></i>
                        </div>
                    </div>
                    <h5>Selecione uma atividade</h5>
                    <p className="text-muted">Escolha uma atividade na lista ao lado ou crie uma nova para gerenciar.</p>
                </div>
            </div>
        );
    }

    return (
        <Card className="border-0 shadow-sm overflow-hidden rounded-0 position-relative">
            <CardHeader className="bg-white py-3 border-bottom sticky-top" style={{ zIndex: 10 }}>
                <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center flex-grow-1 overflow-hidden">
                        <div className="avatar-sm flex-shrink-0">
                            <span className="avatar-title bg-primary bg-gradient text-white rounded shadow-sm">
                                <i className="mdi mdi-run-fast font-size-22"></i>
                            </span>
                        </div>
                        <div className="ms-3 overflow-hidden">
                            <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                                <span className="text-truncate">{formik.values.id ? formik.values.name : "Nova Atividade"}</span>
                                {formik.values.id && (
                                    <Badge color={formik.values.status === GLOBAL_STATUS.ACTIVE ? 'success' : 'danger'} pill className="font-size-10 px-2 flex-shrink-0">
                                        {GLOBAL_STATUS_LABELS[formik.values.status] || 'Desconhecido'}
                                    </Badge>
                                )}
                            </h5>
                            <p className="text-muted mb-0 small text-truncate">Dados básicos e Malha Pedagógica</p>
                        </div>
                    </div>
                    <div className="d-flex gap-2 flex-shrink-0">
                        {formik.values.id && (
                            <Button
                                color="light"
                                className="btn-md rounded-0 px-2 shadow-none text-danger border-0 d-flex align-items-center justify-content-center"
                                style={{ width: '38px', height: '38px' }}
                                onClick={() => onDelete(formik.values.id)}
                                disabled={isSaving}
                                title="Excluir Atividade"
                            >
                                <i className="mdi mdi-trash-can-outline font-size-18"></i>
                            </Button>
                        )}
                        <LoadingButton
                            color="primary"
                            className="btn-md rounded-0 px-4 shadow-sm"
                            onClick={formik.handleSubmit}
                            loading={isSaving}
                            loadingText="Salvando..."
                        >
                            Salvar
                        </LoadingButton>
                    </div>
                </div>
            </CardHeader>

            <CardBody className="p-4">
                <OverlayLoader show={isSaving} label="Salvando..." />
                <Row className="mb-5 border-bottom pb-4">
                    <Col lg={12}>
                        <h6 className="text-uppercase font-size-12 fw-bold text-muted mb-4 d-flex align-items-center">
                            <i className="mdi mdi-information-outline me-2 font-size-16 text-primary"></i>
                            Dados Principais
                        </h6>
                    </Col>

                    <Col md="2" className="text-center mb-4 mb-md-0">
                        <FormPhotoUpload
                            photoPreview={formik.values.photoUrl}
                            onChange={(url) => {
                                formik.setFieldValue("photoUrl", url);
                                onChange(true);
                            }}
                            size="lg"
                        />
                    </Col>

                    <Col md="10">
                        <Row>
                            <Col md="6">
                                <FormGroup className="mb-3">
                                    <Label className="fw-medium text-muted small text-uppercase">Nome da Atividade</Label>
                                    <Input
                                        name="name"
                                        type="text"
                                        className="form-control bg-light-subtle border-0"
                                        placeholder="Ex: Natação, Crossfit..."
                                        {...formik.getFieldProps("name")}
                                        invalid={formik.touched.name && !!formik.errors.name}
                                    />
                                    <FormFeedback>{formik.errors.name}</FormFeedback>
                                </FormGroup>
                            </Col>
                            <Col md="2">
                                <FormGroup className="mb-3">
                                    <Label className="fw-medium text-muted small text-uppercase">Cor</Label>
                                    <Input
                                        name="color"
                                        type="color"
                                        className="form-control p-1 border-0"
                                        style={{ height: '48px' }}
                                        {...formik.getFieldProps("color")}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="2">
                                <FormGroup className="mb-3">
                                    <Label className="fw-medium text-muted small text-uppercase">Total XP</Label>
                                    <Input
                                        name="xp"
                                        type="number"
                                        className="form-control bg-light text-center border-0 font-size-18 fw-bold text-success"
                                        disabled
                                        readOnly
                                        {...formik.getFieldProps("xp")}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="2">
                                <FormGroup className="mb-3">
                                    <Label className="fw-medium text-muted small text-uppercase">Status</Label>
                                    <div className="form-check form-switch pt-2">
                                        <Input
                                            type="switch"
                                            role="switch"
                                            checked={formik.values.status === GLOBAL_STATUS.ACTIVE}
                                            onChange={(e) => {
                                                formik.setFieldValue("status", e.target.checked ? GLOBAL_STATUS.ACTIVE : GLOBAL_STATUS.INACTIVE);
                                                onChange(true);
                                            }}
                                            className="form-check-input custom-switch-primary"
                                        />
                                        <Label className="ms-2 small text-muted">
                                            {GLOBAL_STATUS_LABELS[formik.values.status] || formik.values.status}
                                        </Label>
                                    </div>
                                </FormGroup>
                            </Col>
                            <Col md="12">
                                <FormGroup className="mb-0">
                                    <Label className="fw-medium text-muted small text-uppercase">Descrição / Resumo da Atividade</Label>
                                    <Input
                                        name="description"
                                        type="textarea"
                                        rows="3"
                                        className="form-control bg-light-subtle border-0"
                                        placeholder="Informe um resumo do que esta atividade irá focar, baseado em seus objetivos e tópicos..."
                                        {...formik.getFieldProps("description")}
                                        invalid={formik.touched.description && !!formik.errors.description}
                                    />
                                    <FormFeedback>{formik.errors.description}</FormFeedback>
                                </FormGroup>
                            </Col>
                        </Row>
                    </Col>
                </Row>

                {/* SISTEMA DE AVALIAÇÃO */}
                <div className="bg-light-subtle border rounded-0 p-3 mb-4 d-flex align-items-center justify-content-between">
                    <div>
                        <h6 className="fw-bold mb-1 text-dark d-flex align-items-center gap-2">
                            <i className="mdi mdi-checkbox-marked-circle-outline text-primary font-size-18"></i>
                            Sistema de Avaliação
                        </h6>
                        <p className="text-muted small mb-0 ms-4 text-secondary">
                            Selecione essa chave se essa atividade faz parte do sistema de avaliação.
                        </p>
                    </div>
                    <div className="form-check form-switch pt-1" style={{ transform: "scale(1.2)" }}>
                        <Input
                            type="switch"
                            role="switch"
                            name="evaluationSystem"
                            checked={!!formik.values.evaluationSystem}
                            onChange={(e) => {
                                formik.setFieldValue("evaluationSystem", e.target.checked);
                                onChange(true);
                            }}
                            className="form-check-input custom-switch-primary cursor-pointer"
                        />
                        <Label className="ms-2 text-muted fw-medium mb-0 ms-2" style={{ transform: "scale(0.8)", transformOrigin: "left" }}>
                            {formik.values.evaluationSystem ? 'Sim' : 'Não'}
                        </Label>
                    </div>
                </div>

                {/* MALHA PEDAGÓGICA */}
                <div className="mt-2">
                    <div className="d-flex align-items-center justify-content-between mb-4">
                        <h6 className="text-uppercase font-size-12 fw-bold text-muted mb-0 d-flex align-items-center">
                            <i className="mdi mdi-format-list-bulleted-type me-2 font-size-16 text-primary"></i>
                            Malha Pedagógica Integrada
                        </h6>
                    </div>

                    <ObjectiveGrid
                        objectives={localObjectives}
                        onChange={(newObjectives) => {
                            setLocalObjectives(newObjectives);
                            onChange(true);
                        }}
                    />
                </div>
            </CardBody>
        </Card>
    );
};

ActivityEditor.propTypes = {
    activity: PropTypes.object,
    onSave: PropTypes.func,
    onDelete: PropTypes.func,
    isSaving: PropTypes.bool,
    onChange: PropTypes.func
};

export default ActivityEditor;
